import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { dueDateTimeSlots } from '@/components/tour/notes/data';
import { getUnoccupiedTimeslots, getFirstAvailableTimeSlot } from '../utils/due-date';
import { NotePriority } from '@/components/tour/notes/enums/';
import { priorityDueDateMap } from '../NoteForm';
import { useNotesStore } from '@/components/tour/notes/stores/notes.store';

type Args = {
  availableFrom: Date;
  assigneeId: string;
  backlogEnabled: boolean;
  defaultPriority: NotePriority;
  initialDueDate?: Date;
  noteId?: number;
};

export function useDueDate({
  availableFrom,
  assigneeId,
  backlogEnabled,
  defaultPriority,
  initialDueDate,
  noteId,
}: Args) {
  const notes = useNotesStore((state) => state.notes);
  const [dueDateError, setDueDateError] = useState('');
  const [dueDate, setDueDate] = useState(() => {
    if (initialDueDate) return initialDueDate;

    const priority = priorityDueDateMap[defaultPriority];
    let startDate = dayjs(availableFrom).add(priority, 'day');

    if (!startDate.isSame(availableFrom, 'day')) {
      // If start date is not the same as available from, set it to the first available time slot of the next day
      startDate = startDate.startOf('day').add(dueDateTimeSlots[0], 'minute');
    }

    const firstAvailableTimeSlot = getFirstAvailableTimeSlot(startDate.toDate(), {
      notes,
      assigneeId,
      timeSlots: dueDateTimeSlots,
      maxIterateDays: 30,
    });

    return firstAvailableTimeSlot;
  });

  useEffect(() => {
    const unoccupiedTimeSlots = getUnoccupiedTimeslots(dueDate, {
      notes: notes.filter((n) => {
        if (typeof noteId !== 'number') return true;
        return n.id !== noteId;
      }),
      assigneeId,
      timeSlots: dueDateTimeSlots,
    });

    const availableFromDate = dayjs(availableFrom);
    const currentDate = dayjs(dueDate);
    const dueDateMinutes = currentDate.hour() * 60 + currentDate.minute();

    if (dueDateMinutes % 30 !== 0) {
      setDueDateError('Due time can only be set in 30 minute intervals (12:00, 16:30 etc.)');
      return;
    }

    if (availableFromDate.isAfter(currentDate)) {
      setDueDateError('Due date cannot be set before available from date');
      return;
    }

    if (dueDateMinutes < dueDateTimeSlots[0] || dueDateMinutes > dueDateTimeSlots.at(-1)) {
      setDueDateError('Due date can only be set between 8:00 and 17:00');
      return;
    }

    if (!backlogEnabled && !unoccupiedTimeSlots.includes(dueDateMinutes)) {
      setDueDateError('Time slot is occupied or overlaps with another note');
      return;
    }

    setDueDateError('');
  }, [dueDate, availableFrom, notes, assigneeId, backlogEnabled, noteId]);

  const clearError = () => {
    setDueDateError('');
  };

  return {
    dueDateError,
    dueDate,
    setDueDate,
    clearError,
  };
}
